import type { ReactNode } from 'react';
import { Space } from 'antd';
import type { SpaceProps } from 'antd';
import { SkyLabel } from './SkyLabel';
import type { SkyLabelProps } from './SkyLabel';

/**
 * Props for the SkyLabeledItem component
 */
export interface SkyLabeledItemProps extends Omit<SpaceProps, 'title'> {
  /**
   * The title/label text to display above the content
   * @default ''
   */
  title?: ReactNode;

  /**
   * Optional CSS class name to apply to the title label
   * @default 'text-xs'
   */
  classNameTitle?: string;

  /**
   * Tooltip to display when hovering over the label
   * Can be a string for simple tooltip or TooltipProps object for advanced configuration
   */
  tooltip?: SkyLabelProps['tooltip'];
}

/**
 * SkyLabeledItem component combines SkyLabel with a Space container for organized layout.
 *
 * This component is useful for displaying labeled content in a structured layout with:
 * - A customizable label using SkyLabel
 * - Optional tooltip support
 * - Flexible orientation (vertical or horizontal)
 * - Support for complex children content
 *
 * @example
 * ```tsx
 * // Basic usage
 * <SkyLabeledItem title="Email">
 *   user@example.com
 * </SkyLabeledItem>
 *
 * // Horizontal layout
 * <SkyLabeledItem
 *   title="Status"
 *   orientation="horizontal"
 * >
 *   <span className="text-green-500">Active</span>
 * </SkyLabeledItem>
 *
 * // With tooltip
 * <SkyLabeledItem
 *   title="API Key"
 *   tooltip="Your secret API key"
 * >
 *   sk-1234567890abcdefghijklmnopqrstuvwxyz
 * </SkyLabeledItem>
 *
 * // With custom title styling
 * <SkyLabeledItem
 *   title="Account"
 *   classNameTitle="text-sm font-bold text-blue-400"
 * >
 *   Premium Account
 * </SkyLabeledItem>
 * ```
 */
export const SkyLabeledItem = ({
  children = undefined,
  className = '',
  classNameTitle = 'text-xs text-slate-400',
  orientation = 'vertical',
  title = '',
  tooltip = '',
  size = 0,
  align,
  ...restProps
}: SkyLabeledItemProps) => {
  const titleComponent = (
    <SkyLabel
      tooltip={tooltip}
      className={`font-normal tracking-normal normal-case ${classNameTitle}`}
    >
      {title}
    </SkyLabel>
  );

  let classNameWrapper = '';

  if (orientation === 'horizontal') {
    classNameWrapper = [
      className.includes('justify-') ? '' : 'justify-between',
      className.includes('items-') ? '' : 'items-baseline',
      className.includes('w-') ? '' : 'w-full',
      className,
    ]
      .filter(Boolean)
      .join(' ');
  } else {
    classNameWrapper = [
      // 'flex-col sm:flex-row',
      className.includes('justify-') ? '' : 'justify-between',
      // className.includes('items-') ? '' : 'items-center',
      className.includes('w-') ? '' : 'w-1/2 md:w-full',
      className,
    ]
      .filter(Boolean)
      .join(' ');
  }

  // `Space` defaults `align` to `'center'` for a horizontal orientation when
  // no `align` prop is given, which wins over the Tailwind `items-baseline`
  // utility class above (equal CSS specificity, antd's cssinjs `<style>` tag
  // injected last) and vertically centres taller children — e.g. a `Tag` —
  // instead of lining their text baseline up with the label.
  const mergedAlign = align ?? (orientation === 'horizontal' ? 'baseline' : undefined);

  return (
    <Space
      orientation={orientation}
      align={mergedAlign}
      className={classNameWrapper}
      size={size}
      {...restProps}
    >
      {title ? titleComponent : null}
      {children}
    </Space>
  );
};
